import { MouseEventHandler, useRef, useState } from 'react'
import { styled, theme } from '../../stitches.config'
import usePostOrderStatus from '../../common/hooks/usePostOrderStatus'
import Button from '../../common/components/marketing/buttons/Button'
import MulticolorText from '../../common/components/marketing/text/MulticolorText'
import { Text } from '../../common/components/elements/Text/Text'
import { Field, Form, Formik } from 'formik'
import { TextInputWithLabel } from '../../common/components/elements/Forms/TextInputWithLabel'
import { FormErrorMessage } from '../../common/components/elements/Forms/FormErrorMessage'
import * as yup from 'yup'
import {
    FIELD_ERROR_EMAIL_INVALID,
    FIELD_ERROR_EMAIL_REQUIRED,
    FIELD_ERROR_ORDER_NUMBER_REQUIRED,
} from './strings'
import { Alert } from '../../common/components/elements/Forms/Alert'
import { CORPORATE_ORDER_NUMBER_REGEX } from '@/src/common/constants'
import DefaultModal from '@/src/common/components/marketing/modals/DefaultModal'
import Link from 'next/link'
import { usePartnerTheme } from '@/src/common/contexts/PartnerThemeContext'

const Container = styled('div', {
    display: 'flex',
    jc: 'center',
    bc: '$lightest',
})

const ContentContainer = styled('div', {
    display: 'flex',
    fd: 'column',
    jc: 'space-between',
    width: '90%',
    maxWidth: '1200px',
    margin: 'auto',
    mb: '20px',
    columnGap: '50px',
    '@md': {
        fd: 'row',
    },
})

const LeftContent = styled('div', {
    display: 'flex',
    fd: 'column',
    flex: 0.6,
    p: '30px 0 0',
    '@md': {
        maxWidth: '550px',
        p: '40px 0',
    },
    '.form-body-text': {
        marginTop: '1rem',
        lineHeight: '125%',
        '@media (max-width: 425px)': {
            fontSize: '1.1rem',
        },
    },
})

const RightContent = styled('div', {
    display: 'flex',
    fd: 'column',
    flex: 0.4,
    p: '20px 0',
    '@md': {
        p: '40px 0',
    },
})

const StyledLine = styled('div', {
    maxWidth: '65px',
    maxHeight: 0,
    border: '1px solid $secondary',
})

const FormRowWrapper = styled('div', {
    display: 'flex',
    ai: 'left',
    gap: '.3em',
    mt: '1em',
    fd: 'column',
})

const ModalContentWrapper = styled('div', {
    m: 0,
    '.modal-header': {
        fontWeight: 'bold',
        fontSize: '1.45rem',
        lh: '2rem',
        my: 0,
        '@media (max-width: 455px)': {
            mt: 10,
        },
    },
})

const INSTRUCTION_TEXT = `Input your order number (XXXXXX-XXXXXX) and email 
                          address used to place the order to receive information 
                          such as gift card type, gift card denominations, order 
                          status, and delivery method for each card.`

const getEmailParam: Function = () => {
    if ('undefined' === typeof window) {
        return {
            email: '',
        }
    }
    const urlParams = new URLSearchParams(window?.location?.search)
    const email = urlParams.get('email')

    return email ?? ''
}

const getOrderParam: Function = () => {
    if ('undefined' === typeof window) {
        return {
            order: '',
        }
    }
    const urlParams = new URLSearchParams(window?.location?.search)
    const order = urlParams.get('order')

    return order ?? ''
}

interface OrderStatusFormProps {
    setOrderStatusDetails: Function
}

const OrderStatusForm = ({ setOrderStatusDetails }: OrderStatusFormProps) => {
    const formRef: any = useRef(null)
    const { mutate: submitOrderNumber } = usePostOrderStatus()
    const [submitButtonEnabled, setSubmitButtonEnabled] = useState(true)
    const [submissionError, setSubmissionError] = useState('')
    const [showCorporateMessage, setShowCorporateMessage] =
        useState<boolean>(false)
    const email = getEmailParam()
    const order = getOrderParam()
    const partnerTheme = usePartnerTheme()

    const SubmitButton = (
        isSubmitting: boolean,
        submitForm: MouseEventHandler<HTMLButtonElement>,
        dataTestId?: string | undefined
    ) => (
        <Button
            css={{ marginTop: '20px', textTransform: 'uppercase' }}
            variant={'primary'}
            size={'md'}
            width={'full'}
            showArrow
            type="submit"
            value="check-order-status"
            data-testid={dataTestId ?? 'submitButton'}
            label={
                !submitButtonEnabled ? 'Submitting...' : 'Check my order status'
            }
            disabled={!submitButtonEnabled}
            onClick={() => submitForm}
        />
    )

    return (
        <Container>
            <DefaultModal
                showModal={showCorporateMessage}
                setShowModal={setShowCorporateMessage}
                testId={'corporate-modal'}
            >
                <ModalContentWrapper>
                    <p data-testid={'modal-header'} className={'modal-header'}>
                        Looking for an update on your{' '}
                        <Link
                            href={'https://www.buygiftcards.com'}
                            className={'gradiant-text'}
                        >
                            BuyGiftCards.com
                        </Link>{' '}
                        order?
                    </p>
                    <p data-testid={'modal-body'}>
                        Please view your order history within the{' '}
                        <Link
                            href={'https://corp.perfectgift.com/orders/history'}
                        >
                            Corporate portal
                        </Link>
                        , contact your account executive,{' '}
                        <a href={'mailto:corp@perfectgift.com'}>email us</a>{' '}
                        <span className={'bold'}>or call</span>{' '}
                        <a href="tel:+18774484438" data-phone={'phone-number'}>
                            (877) 448-4438
                        </a>{' '}
                        for additional order information or support.
                    </p>
                </ModalContentWrapper>
            </DefaultModal>
            <ContentContainer>
                <LeftContent>
                    <StyledLine />
                    <MulticolorText
                        startText={'Check your'}
                        colorText={'order status'}
                        secondaryColor={{
                            useGradient: true,
                            gradient: {
                                rotation: 63,
                                // @ts-ignore
                                firstColor: partnerTheme?.colors?.primary?.value ?? theme.colors.primary.value,
                                firstColorPercent: 19,
                                // @ts-ignore
                                secondColor: partnerTheme?.colors?.secondary?.value ?? theme.colors.secondary.value,
                                secondColorPercent: 82,
                            },
                        }}
                        htmlElement={'h1'}
                        additionalStyles={{
                            fontSize: theme.fontSizes['8'],
                            margin: '1rem 0',
                            '@md': {
                                fontSize: theme.fontSizes['9'],
                            },
                        }}
                    />
                    <Text
                        data-testid={'instructions'}
                        className={'form-body-text'}
                        size={'5'}
                    >
                        {INSTRUCTION_TEXT}
                    </Text>
                </LeftContent>
                <RightContent>
                    <Formik
                        initialValues={{
                            order_number: order,
                            email_address: email,
                        }}
                        validationSchema={yup.object({
                            order_number: yup
                                .string()
                                .required(FIELD_ERROR_ORDER_NUMBER_REQUIRED),
                            email_address: yup
                                .string()
                                .email(FIELD_ERROR_EMAIL_INVALID)
                                .required(FIELD_ERROR_EMAIL_REQUIRED),
                        })}
                        onSubmit={async (values) => {
                            setShowCorporateMessage(
                                values.order_number.match(
                                    CORPORATE_ORDER_NUMBER_REGEX
                                )
                            )

                            setSubmitButtonEnabled(false)

                            submitOrderNumber(values, {
                                onSuccess: (data) => {
                                    setOrderStatusDetails(data)
                                    setSubmitButtonEnabled(true)
                                },
                                onError: (error: any) => {
                                    switch (error?.response?.status) {
                                        case 404:
                                            setSubmissionError(
                                                'Order not found. Please make sure the order number and email used to place the order are correct.'
                                            )
                                            break
                                        default:
                                            setSubmissionError(
                                                'Something went wrong, please try again.'
                                            )
                                            break
                                    }

                                    setSubmitButtonEnabled(true)
                                },
                            })
                        }}
                        innerRef={formRef}
                    >
                        {({ isSubmitting, errors, touched, submitForm }) => (
                            <Form
                                onChange={() => {
                                    setSubmissionError('')
                                }}
                                data-testid={'order-form'}
                            >
                                {submissionError && (
                                    <Alert
                                        variant={'error'}
                                        data-testid={
                                            'order-status-submit-error'
                                        }
                                        label={submissionError}
                                        css={{ md: '$3', maxWidth: '25em' }}
                                    />
                                )}
                                <FormRowWrapper>
                                    <Text>Get your order status:</Text>
                                </FormRowWrapper>
                                <FormRowWrapper>
                                    <Field name="order_number">
                                        {({ field, meta }: any) => (
                                            <TextInputWithLabel
                                                {...field}
                                                {...meta}
                                                label={'Order number'}
                                                autoComplete={'off'}
                                                data-testid={'order-number'}
                                            />
                                        )}
                                    </Field>
                                    {!!errors.order_number &&
                                        touched.order_number && (
                                            <FormErrorMessage
                                                data-testid={'err-order-number'}
                                            >
                                                {/*@ts-ignore*/}
                                                {errors.order_number}
                                            </FormErrorMessage>
                                        )}
                                </FormRowWrapper>
                                <FormRowWrapper>
                                    <Field name="email_address">
                                        {({ field, meta }: any) => (
                                            <TextInputWithLabel
                                                {...field}
                                                {...meta}
                                                label={'Email address'}
                                                autoComplete={'off'}
                                                data-testid={'email_address'}
                                            />
                                        )}
                                    </Field>
                                    {!!errors.email_address &&
                                        touched.email_address && (
                                            <FormErrorMessage
                                                data-testid={'err-email'}
                                            >
                                                {/*@ts-ignore*/}
                                                {errors.email_address}
                                            </FormErrorMessage>
                                        )}
                                </FormRowWrapper>
                                {SubmitButton(isSubmitting, submitForm)}
                            </Form>
                        )}
                    </Formik>
                </RightContent>
            </ContentContainer>
        </Container>
    )
}

export default OrderStatusForm
